import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import { fadeUp, viewport } from "@/lib/motion";
import { packages, WHATSAPP_NUMBER, WHATSAPP_MSG } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeadForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "", package: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "contact_form" });
      setDone(true);
      toast.success("Got it! We'll be in touch within a few hours.");
    } catch {
      toast.error("Couldn't send that. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MSG)}`;

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#0a0a0a] border-t border-white/10" data-testid="lead-form">
      <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-start">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}>
          <div className="inline-flex items-center gap-2 rounded-full bg-[#ccff00]/15 border border-[#ccff00]/40 text-[#ccff00] text-xs font-semibold uppercase tracking-widest px-4 py-2 mb-6">
            <Clock size={14} /> Order today → live in 48 hours
          </div>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
            Let's get your business launched.
          </h2>
          <p className="text-lg text-zinc-400 mb-8 max-w-lg">
            Tell us what you're starting. We'll come back with the right package and get your brand, website and
            online presence live — fast. Prefer to chat? WhatsApp us.
          </p>
          <a href={waHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white font-medium text-sm px-6 py-4 hover:bg-white/5 transition-colors" data-testid="lead-whatsapp">
            💬 Message us on WhatsApp
          </a>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}>
          {done ? (
            <div className="bg-[#121212] border border-[#ccff00]/40 rounded-2xl p-10 text-center volt-glow" data-testid="lead-success">
              <p className="font-display font-black text-3xl mb-3">You're in! 🎉</p>
              <p className="text-zinc-400">Thanks {form.name.split(" ")[0]} — we've got your details and we'll be in touch within a few hours to get your 48-hour launch started.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-[#121212] border border-white/10 rounded-2xl p-6 md:p-8 space-y-4" data-testid="lead-form-fields">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your name *" value={form.name} onChange={set("name")} placeholder="Sarah Jones" testid="lead-name" />
                <Field label="Email *" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" testid="lead-email" />
                <Field label="Phone" value={form.phone} onChange={set("phone")} placeholder="07…" testid="lead-phone" />
                <Field label="Business name / idea" value={form.business} onChange={set("business")} placeholder="Sparkle Cleaning" testid="lead-business" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Package</label>
                <select value={form.package} onChange={set("package")} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3.5 text-white focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none" data-testid="lead-package">
                  <option value="">Not sure yet — help me choose</option>
                  {packages.map((p) => <option key={p.id} value={p.name}>{p.name} — {p.price}</option>)}
                  <option value="Membership">Monthly care plan</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Anything else?</label>
                <textarea value={form.message} onChange={set("message")} rows={3} placeholder="Tell us about your business, colours you like, businesses you admire…" className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none resize-none" data-testid="lead-message" />
              </div>
              <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 hover:scale-[1.01] transition-transform disabled:opacity-60" data-testid="lead-submit">
                {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <><Send size={18} /> Start my 48-hour launch</>}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const Field = ({ label, type = "text", value, onChange, placeholder, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">{label}</label>
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none transition-shadow" data-testid={testid} />
  </div>
);

export default LeadForm;
